import { createContext, useContext, useEffect, useState } from "react";

import { getUser } from "@/lib/api";
import { urlLocaleOf } from "@/lib/localePath";
import de from "@/lib/i18n/de.js";
import el from "@/lib/i18n/el.js";
import en from "@/lib/i18n/en.js";
import es from "@/lib/i18n/es.js";
import fr from "@/lib/i18n/fr.js";
import it from "@/lib/i18n/it.js";
import pt from "@/lib/i18n/pt.js";

/**
 * Il dizionario: un modulo per lingua, uniti qui.
 *
 * Ogni file è un albero di chiavi identico agli altri (402 foglie), e
 * l'ordine di questo oggetto è l'ordine in cui le lingue compaiono nel
 * selettore della barra. Aggiungere una lingua significa un file in
 * `src/lib/i18n/` più il codice in `APP_LOCALES` (`backend/jurisdictions.py`):
 * il test `test_le_lingue_dichiarate_sono_quelle_tradotte` confronta le due cose
 * e fallisce se una delle due dimentica l'altra.
 *
 * L'estrazione dai blocchi precedenti è servita a due cose: un file da ~2000
 * righe è un conflitto garantito su ogni traduzione, e `pt`/`el` raddoppierebbero
 * il problema. Il provider non sa nulla di quali lingue esistono: le legge da
 * qui.
 */
const dict = { it, en, es, fr, de, pt, el };

// ---------------------------------------------------------------- lingua
// Le lingue che l'interfaccia sa mostrare sono le chiavi del dizionario, e
// nient'altro: aggiungere un codice senza tradurlo mostrerebbe `nav.pricing`
// al posto della parola. Il backend tiene la stessa lista in
// `jurisdictions.APP_LOCALES`, e un test confronta le due.
export const SUPPORTED_LANGS = Object.keys(dict);

//: Il nome di ogni lingua **nella propria lingua**. Serve a chi non riconosce
//: l'abbreviazione `DE` in una barra: cercare il tedesco e non trovarlo è il
//: motivo per cui un selettore resta fermo sull'inglese. Sono esonimi locali,
//: quindi non passano dal dizionario (che è ciò che stiamo scegliendo).
export const LANG_NAMES = {
  it: "Italiano", en: "English", es: "Español", fr: "Français", de: "Deutsch",
  pt: "Português", el: "Ελληνικά",
};

//: La scelta esplicita dell'utente. Solo `setLang` la scrive: una lingua
//: indovinata dal browser non deve diventare una decisione permanente, altrimenti
//: chi viaggia vedrebbe l'interfaccia bloccarsi sulla lingua dell'hotel.
export const LANG_STORAGE_KEY = "hh_lang";
export const DEFAULT_LANG = "it";

//: Il paese suggerito dall'edge → lingua locale. Solo un suggerimento: una casa
//: in Spagna ha obblighi spagnoli anche se l'host legge dall'Italia.
const LANG_BY_COUNTRY = {
  IT: "it", ES: "es", FR: "fr", DE: "de", AT: "de", PT: "pt", GR: "el",
  GB: "en", CH: "de", US: "en",
};

/** `es-MX` / `ES_mx` → `es`; `null` quando la lingua non è nel dizionario. */
export function normalizeLang(value) {
  const base = String(value || "").trim().toLowerCase().replace("_", "-").split("-")[0];
  return SUPPORTED_LANGS.includes(base) ? base : null;
}

/** `Accept-Language` → la prima lingua che sappiamo mostrare, rispettando i pesi. */
export function fromAcceptLanguage(header) {
  if (!header) return null;
  const entries = String(header).split(",").map((part, index) => {
    const [tag, ...params] = part.trim().split(";");
    const q = params.map((p) => p.trim()).find((p) => p.startsWith("q="));
    const weight = q ? Number.parseFloat(q.slice(2)) : 1;
    return { tag, weight: Number.isFinite(weight) ? weight : 1, index };
  }).filter((e) => e.tag);
  // A parità di peso conta l'ordine: `it,en;q=0.9` significa italiano.
  entries.sort((a, b) => b.weight - a.weight || a.index - b.index);
  for (const entry of entries) {
    const code = normalizeLang(entry.tag);
    if (code) return code;
  }
  return null;
}

/** Il paese dell'edge → lingua, ma solo se quella lingua è tradotta. */
export function langFromCountry(country) {
  const hint = LANG_BY_COUNTRY[String(country || "").trim().toUpperCase()];
  return normalizeLang(hint);
}

/**
 * La lingua da mostrare, in ordine di forza:
 *
 * 1. `?lang=` nell'URL: e' una scelta esplicita messa in un link, e deve
 *    funzionare anche per chi ha una preferenza salvata (le cinque lingue che
 *    non hanno un indirizzo proprio passano da qui);
 * 2. il prefisso dell'indirizzo (`/en/pricing`): chi apre la pagina inglese
 *    deve leggere l'inglese, anche se sul suo dispositivo l'italiano era
 *    stato scelto mesi fa. E' il cambio che ha reso necessari gli indirizzi
 *    per lingua: prima l'URL non poteva dire niente sulla lingua;
 * 3. la scelta salvata (`localStorage.hh_lang`);
 * 4. il `locale` del profilo, se autenticato;
 * 5. `Accept-Language` / `navigator.languages`;
 * 6. il paese suggerito dall'edge — mai per un obbligo fiscale.
 *
 * `urlLocale` e non `urlLang` per il prefisso: un `?lang=de` su `/en/pricing`
 * e' ancora un override esplicito, un prefisso invece e' la lingua della
 * pagina e basta. Sulle rotte tecniche `urlLocale` e' `null`, quindi li' vale
 * la preferenza salvata come prima di questo modulo.
 */
export function resolveLang({ stored, urlLocale, urlLang, profile, browser, country } = {}) {
  const browserList = Array.isArray(browser) ? browser : [browser];
  return normalizeLang(urlLang)
    || normalizeLang(urlLocale)
    || normalizeLang(stored)
    || normalizeLang(profile)
    || browserList.map(normalizeLang).find(Boolean)
    || langFromCountry(country)
    || DEFAULT_LANG;
}

const readExplicitLang = () => { try { return localStorage.getItem(LANG_STORAGE_KEY); } catch { return null; } };
const writeExplicitLang = (code) => { try { localStorage.setItem(LANG_STORAGE_KEY, code); } catch { /* storage pieno o negato */ } };
const readUrlLang = () => {
  try { return new URLSearchParams(window.location.search).get("lang"); } catch { return null; }
};
// Il prefisso della sezione linguistica, solo dove esiste (`/it`, `/en`).
const readUrlLocale = () => {
  try { return urlLocaleOf(window.location.pathname); } catch { return null; }
};
const browserLangs = () => {
  if (typeof navigator === "undefined") return [];
  return [...(navigator.languages || []), navigator.language].filter(Boolean);
};
// `getUser` arriva da `@/lib/api`, che molti test sostituiscono con un finto
// parziale: un binding mancante non deve impedire di montare una pagina.
const profileLang = () => {
  if (typeof getUser !== "function") return null;
  try { return getUser()?.locale || null; } catch { return null; }
};

const LangContext = createContext({ lang: DEFAULT_LANG, setLang: () => {}, t: (k) => k });

export function LanguageProvider({ children, suggestLang, saveLang }) {
  const [lang, setLangState] = useState(() => resolveLang({
    stored: readExplicitLang(),
    urlLocale: readUrlLocale(),
    urlLang: readUrlLang(),
    profile: profileLang(),
    browser: browserLangs(),
  }));

  // Il paese dell'edge è l'ultimo segnale e arriva dalla rete: si chiede una
  // volta, e si usa solo se nessuno degli altri ha già parlato. La funzione la
  // passa `App` (`localeApi.js`): il dizionario non fa chiamate HTTP, così un
  // test che monta il provider non deve fingere l'intero strato API.
  useEffect(() => {
    if (typeof suggestLang !== "function") return undefined;
    // Un indirizzo per lingua e una scelta salvata sono gia' una risposta: il
    // suggerimento dell'edge non deve nemmeno essere chiesto.
    if (readExplicitLang() || readUrlLocale() || normalizeLang(readUrlLang())) return undefined;
    let alive = true;
    Promise.resolve()
      .then(() => suggestLang())
      .then((suggested) => {
        const next = normalizeLang(suggested);
        if (alive && next && !readExplicitLang() && !readUrlLocale() && !normalizeLang(readUrlLang())) {
          setLangState(next);
        }
      })
      .catch(() => {}); // niente paese: resta il default
    return () => { alive = false; };
  }, [suggestLang]);

  useEffect(() => { document.documentElement.lang = lang; }, [lang]);

  const setLang = (code) => {
    const next = normalizeLang(code);
    if (!next) return;
    setLangState(next);
    writeExplicitLang(next);
    // La stessa persona da un altro dispositivo ritrova la sua lingua. È
    // best-effort: un salvataggio fallito non deve impedire il cambio né
    // mostrare un errore per una preferenza.
    if (typeof saveLang === "function") {
      try { Promise.resolve(saveLang(next)).catch(() => {}); } catch { /* best-effort */ }
    }
  };

  // `params` sostituisce i segnaposto `{nome}`: alcune stringhe (il periodo del
  // gruppo, il numero di membri) non si possono comporre a chiamata.
  const t = (key, params) => {
    const parts = key.split(".");
    let cur = dict[lang];
    for (const p of parts) { if (cur && p in cur) cur = cur[p]; else return key; }
    if (params && typeof cur === "string") {
      return cur.replace(/\{(\w+)\}/g, (m, k) => (params[k] === undefined ? m : params[k]));
    }
    return cur;
  };
  return <LangContext.Provider value={{ lang, setLang, t }}>{children}</LangContext.Provider>;
}

export const useLang = () => useContext(LangContext);
